import { useCallback, useMemo, useState, useEffect } from 'react';
import { Col, Form, Row } from 'antd';
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ErrorHelper from 'helpers/ErrorHelper';
import { useRecoilValue } from 'recoil';
import { getShoppingCenters } from 'hooks/useUser';
import Calendars from 'libs/api/admin/calendars';

import { useApi } from 'hooks/useApi';
import UIHelper from 'helpers/UIHelper';

import CalendarsAdapter from './calendars.adapter';
import { Button } from 'components';
import { EditIcon } from 'icons';
import { EyeOutlined } from '@ant-design/icons';
import { useFilters } from './useFilters';
import moment from 'moment';
import { getPage } from 'hooks/useUser';

const api = new Calendars();

const useCalendars = (isMainPage = true) => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const { filters, groupBy } = useFilters();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const shoppingCenters = useRecoilValue(getShoppingCenters);
  const pages = useRecoilValue(getPage);
  const [singleData, setSingleData] = useState([]);

  const {
    data,
    loading,
    setLoading,
    updateInProcess,
    setUpdateInProcess,
    handleDownloadExcel,
    onChangeTableFilter,
    onDelete: handleDeleteCategory,
  } = useApi({
    resourceKey: 'calendars',
    singleResourceKey: 'calendars',
    api,
    isMainPage,
    adapter: CalendarsAdapter,
  });

  const columns = useMemo(() => {
    return [
      {
        title: t('Settimana'),
        dataIndex: 'calendar_date',
        sorter: () => {},
        width: 130,
        render: (row) => {
          switch (groupBy) {
            case 'day':
              return UIHelper.formatedDate(row, 'DD MMMM YYYY');
            case 'week':
              return `${t('Settimana')} ${moment(
                row,
              ).isoWeek()}, ${UIHelper.formatedDate(row, 'YYYY')}`;
            case 'month':
              return UIHelper.formatedDate(row, 'MMMM YYYY');
            case 'year':
              return UIHelper.formatedDate(row, 'YYYY');
            default:
              return `${t('Settimana')} ${moment(
                row,
              ).isoWeek()}, ${UIHelper.formatedDate(row, 'YYYY')}`;
          }
        },
      },
      {
        title: t('Data'),
        dataIndex: 'calendar_date',
        width: 180,
        sorter: () => {},
        render: (row) => UIHelper.formatedDate(row),
      },
      {
        title: t('Giorno'),
        dataIndex: 'calendar_date',
        width: 130,
        sorter: () => {},
        render: (data, i) => (
          <span style={{ textTransform: 'capitalize' }}>
            {moment(i.calendar_date).format('dddd')}
          </span>
        ),
      },
      {
        title: t('Aperture'),
        dataIndex: 'type_close',
        width: 200,
        sorter: () => {},
      },
      {
        title: t('Tipo Giorno'),
        dataIndex: 'day_type',
        width: 130,
        sorter: () => {},
      },
      {
        title: t('Footfall'),
        dataIndex: 'footfalls.visitors',
        width: 100,
        sorter: () => {},
      },
      {
        title: t('Trading calendar'),
        dataIndex: 'analytics_calendar_categories.title',
        width: 200,
        sorter: () => {},
      },
      {
        title: t('Note'),
        dataIndex: 'comment',
        width: 200,
        sorter: () => {},
      },
      {
        title: '',
        dataIndex: 'address',
        align: 'right',
        width: 120,
        fixed: 'right',
        render: (data, row) => {
          const group = groupBy || 'day';
          const viewUrl = `${pathname}/${row.calendar_date}?by_group=${group}`;
          const editUrl = `${pathname}/${row.calendar_date}/edit?by_group=${group}`;
          return (
            (pages?.ability?.can_read !== false ||
              row.can_read !== false ||
              row.can_update !== false) && (
              <Row
                gutter={16}
                justify={'center'}
                onClick={(e) => e.stopPropagation()}
              >
                {(pages?.ability?.can_read !== false ||
                  row.can_read !== false) && (
                  <Col>
                    <Link to={viewUrl}>
                      <Button type={'secondary'} icon={<EyeOutlined />} />
                    </Link>
                  </Col>
                )}
                {row.can_update !== false && (
                  <Col>
                    <Link to={editUrl}>
                      <Button type={'secondary'} icon={<EditIcon />} />
                    </Link>
                  </Col>
                )}
              </Row>
            )
          );
        },
      },
    ];
  }, [t, pathname, groupBy, pages?.ability?.can_read]);

  const handleCreateOrUpdate = useCallback(
    async (values, day, shoppingCenterId) => {
      setUpdateInProcess(true);
      try {
        const data = {
          footfall: {
            ...values,
          },
        };
        if (!day) {
          await api.create({ data });
          navigate('/admin/footfalls');
        } else {
          const data = {
            footfalls: {
              ...values,
            },
          };
          await api.updateEdit(data, day, shoppingCenterId);
          navigate('/admin/footfalls');
        }
      } catch (e) {
        form.setFields(
          ErrorHelper.getErrorsForForm(e?.response?.data || {}, {
            shopping_center: 'shopping_center_id',
          }),
        );
      } finally {
        setUpdateInProcess(false);
      }
    },
    [form, navigate, setUpdateInProcess],
  );

  const handleImportExcel = useCallback(
    async (values) => {
      setUpdateInProcess(true);
      try {
        const formData = new FormData();
        for (let key in values) {
          if (key !== 'file') {
            formData.append(`${key}`, values[key] || '');
          } else {
            if (values[key]) {
              formData.append(`${key}`, values[key][0]?.originFileObj || '');
            }
          }
        }
        await api.create({
          data: formData,
        });
        navigate('/admin/calendars');
      } catch (e) {
        console.log('er', e);
        const errors = e?.response?.data || [];
        if (errors.length) {
          form.setFields([
            {
              name: 'shopping_center_id',
              errors,
            },
          ]);
        } else {
          form.setFields([
            {
              name: 'file',
              errors: [t('Wrong File')],
            },
          ]);
        }
      } finally {
        setUpdateInProcess(false);
      }
    },
    [form, navigate, setUpdateInProcess, t],
  );

  useEffect(() => {
    (async () => {
      if (!isMainPage) {
        // setLoading(true);
        // try {
        //   const { data } = await api.getEdit(day, shoppingCentersId);
        //
        //   if (Array.isArray(data?.footfalls)) {
        //     setSingleData((d) => [...d, ...data.footfalls]);
        //     form.setFields(
        //       data.footfalls.map((footfall) => ({
        //         name: footfall.id,
        //         value: footfall.visitors,
        //       })),
        //     );
        //   }
        // } catch (e) {
        //   form.setFields(
        //     ErrorHelper.getErrorsForForm(e?.response?.data || {}, {
        //       shopping_center: 'shopping_center_id',
        //     }),
        //   );
        // } finally {
        //   setLoading(false);
        // }
      }
    })();
  }, [isMainPage, setLoading, form]);

  return {
    columns,
    data,
    singleData,
    updateInProcess,
    loading,
    onChangeTableFilter,
    form,
    shoppingCenters,
    handleCreateOrUpdate,
    handleDeleteCategory,
    handleDownloadExcel,
    handleImportExcel,
    filters,
  };
};

export default useCalendars;
